// Cohort dashboard — mirrors intake/sheet-schema.md §"Dashboard" metrics.
// Champion+ only (server-side gated; if forbidden, the API returns 403).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Dashboard as Dash } from "../lib/api";

const LEVEL_ORDER = [
  "Novice", "Apprentice", "Practitioner", "Power User", "Champion", "Ambassador",
];

export function Dashboard() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery<Dash>({
    queryKey: ["dashboard"],
    queryFn: api.dashboard.cohort,
  });

  const recompute = useMutation({
    mutationFn: api.levels.recompute,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-700">Failed: {(error as Error).message}</p>;
  if (!data) return null;

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Cohort dashboard</h1>
        <div className="text-xs text-slate-500 flex items-center gap-3">
          <span>as of {new Date(data.as_of).toLocaleString()}</span>
          <button
            className="underline hover:text-slate-700"
            disabled={recompute.isPending}
            onClick={() => recompute.mutate()}
          >
            {recompute.isPending ? "Recomputing…" : "Recompute levels"}
          </button>
          <button className="underline" onClick={() => refetch()}>Refresh</button>
        </div>
      </div>

      {/* KPI cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Active seats this week" value={data.active_seats_this_week} />
        <KPI label="Weekly Active Users (≥3 days)" value={data.weekly_active_users} />
        <KPI label="Onboarding rate" value={pct(data.onboarding_rate)} />
        <KPI label="Hours saved (90d)" value={data.total_hours_saved_90d.toFixed(1)} />
        <KPI label="High-impact wins (90d)" value={data.high_impact_wins_90d} />
        <KPI label="Pending attestations" value={data.active_pending_attestations} highlight={data.active_pending_attestations > 0} />
        <KPI
          label="Median attestation latency"
          value={data.attestation_latency_median_days != null
            ? `${data.attestation_latency_median_days.toFixed(1)}d`
            : "—"}
        />
        <KPI label="Pilot cohort size" value={data.pilot_cohort.head_count} />
      </section>

      {/* Level distribution */}
      <section>
        <h2 className="font-medium mb-2">Level distribution</h2>
        <LevelDistributionBar dist={data.level_distribution} />
      </section>

      {/* Per-function pivot */}
      <section>
        <h2 className="font-medium mb-2">Per-function</h2>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-300 text-left">
            <tr>
              <th className="py-2">Function</th>
              <th className="text-right">Head</th>
              <th className="text-right">Active</th>
              <th className="text-right">WAU</th>
              <th className="text-right">Hrs saved 90d</th>
              <th className="text-right">High-impact 90d</th>
              <th className="text-right">Practitioner+</th>
            </tr>
          </thead>
          <tbody>
            {data.per_function.map((f) => (
              <tr key={f.function} className="border-b border-slate-100">
                <td className="py-1">{f.function}</td>
                <td className="text-right">{f.head_count}</td>
                <td className="text-right">{f.active_seats_this_week}</td>
                <td className="text-right">{f.weekly_active_users}</td>
                <td className="text-right">{f.hours_saved_90d.toFixed(1)}</td>
                <td className="text-right">{f.high_impact_wins_90d}</td>
                <td className="text-right">{pct(f.practitioner_plus_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Pilot cohort slice */}
      <section>
        <h2 className="font-medium mb-2">Pilot cohort (Q1 Foundation)</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KPI label="Active seats this week" value={data.pilot_cohort.active_seats_this_week} />
          <KPI label="WAU" value={data.pilot_cohort.weekly_active_users} />
          <KPI label="Onboarding rate" value={pct(data.pilot_cohort.onboarding_rate)} />
          <KPI label="Hours saved (90d)" value={data.pilot_cohort.total_hours_saved_90d.toFixed(1)} />
          <KPI label="High-impact (90d)" value={data.pilot_cohort.high_impact_wins_90d} />
          <KPI label="Head count" value={data.pilot_cohort.head_count} />
        </div>
      </section>
    </div>
  );
}

function KPI({ label, value, highlight }: { label: string; value: number | string; highlight?: boolean }) {
  return (
    <div className={`rounded border p-3 ${highlight ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-slate-600 mt-1">{label}</div>
    </div>
  );
}

function LevelDistributionBar({ dist }: { dist: { level: string; count: number }[] }) {
  // Normalize order; missing levels render with 0.
  const byLevel = Object.fromEntries(dist.map((d) => [d.level, d.count]));
  const rows = LEVEL_ORDER.map((l) => ({ level: l, count: byLevel[l] ?? 0 }));
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.level} className="flex items-center gap-2 text-sm">
          <div className="w-28">{r.level}</div>
          <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
            <div
              className="bg-slate-700 h-full"
              style={{ width: total > 0 ? `${(r.count / total) * 100}%` : "0%" }}
            />
          </div>
          <div className="w-10 text-right text-slate-700">{r.count}</div>
        </div>
      ))}
    </div>
  );
}

function pct(r: number): string {
  return `${(r * 100).toFixed(0)}%`;
}
